/**
 * LocaleSwitcher — accessible language picker for marketing portals.
 *
 * Pattern: button + listbox dropdown (WAI-ARIA Authoring Practices
 * "collapsible listbox" widget). When the user picks a language we:
 *   1. Call react-i18next's i18n.changeLanguage(lang)
 *   2. Persist the choice via the storage adapter (so it survives
 *      reloads + cross-tab sync via the storage event)
 *   3. Fire the optional onChange callback so the consumer can sync
 *      analytics / OpenTelemetry baggage / etc.
 *
 * Phase 27 ships English + Spanish on port-web (the first ecosystem
 * app with multi-language marketing copy). Future marketing portals
 * pull this component in and get ES support for free.
 *
 * **POPOVER-FACTORY-001:** the open/positioning/dismiss machinery rides the
 * canonical `@puffin-core/popover` factory (DRP-001) — open-on-click toggle
 * (`useClick`), outside-click/Escape dismiss (`useDismiss`), and positioning
 * (`position: fixed` via floating-ui) all come from the factory, NOT a
 * hand-rolled `position:absolute` + document `mousedown` listener. The trigger
 * advertises `aria-haspopup="listbox"` via `triggerHasPopup="listbox"`.
 *
 * **POPOVER-LISTNAV-001:** the roving keyboard navigation (Arrow / Home / End +
 * roving focus) now rides the factory's opt-in `listNavigation` API instead of a
 * hand-rolled `handleItemKeyDown` switch — the consumer holds `activeIndex` +
 * `itemRefs` (wired into the descriptor's `listRef`) and spreads `itemProps(i)`
 * onto each option; floating-ui owns Arrow/Home/End and moves focus to the active
 * item. ONLY the roving nav is migrated. Everything else — storage persistence,
 * cross-tab `storage` sync, SSR `initialLocale`, blog-path-aware navigation, the
 * Enter/Space-selects activation, `handleSelect`, the `setTimeout` focus-on-open
 * landing spot, and the ArrowUp/Down-opens-the-listbox trigger nicety — is
 * orthogonal to positioning/nav and is kept untouched.
 *
 * @module @puffin-core/marketing-shell/LocaleSwitcher
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent } from '@puffin-core/popover';
import { isBlogPath, stripLocalePrefix, withLocalePrefix } from '../blog/blogLocalePath.js';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

const KEY_ENTER = 'Enter';
const KEY_SPACE = ' ';
const KEY_ARROW_UP = 'ArrowUp';
const KEY_ARROW_DOWN = 'ArrowDown';

/**
 * useLocaleLanguage — the "which language is selected" half of the switcher:
 * resolves the initial locale (SSR seed → storage → i18n → first configured),
 * holds the selected code + the roving active index, applies the code to i18n,
 * and keeps both in sync with sibling tabs via the `storage` event.
 *
 * @returns {{currentLang: string, setCurrentLang: Function,
 *            activeIndex: number, setActiveIndex: Function}}
 */
function useLocaleLanguage({
  languages,
  storageKey,
  storage,
  STORAGE_TYPE_LOCAL,
  initialLocale,
  i18n,
}) {
  const initialLang = useMemo(() => {
    const codes = languages.map((l) => l.code);
    // BLOG-STD-001: on a publish-time-prerendered + hydrated page, `initialLocale`
    // is seeded from the URL locale (the `<script type="application/json"
    // id="__blog-ssr-seed__">` data block's `locale`) and takes precedence over
    // the stored pref, so the client's first render matches the server-rendered
    // switcher exactly (zero hydration mismatch). Absent it (normal CSR), the
    // storage-first behavior below is unchanged.
    if (initialLocale && codes.includes(initialLocale)) return initialLocale;
    // `storage` is optional — the prerender harness renders the switcher
    // with no browser storage (it relies on `initialLocale` above), so guard it.
    // It is the site's StorageService INSTANCE (N198 batch 6: no process-wide singleton), read
    // with its own `get`: the switcher used to call `getItem` / `setItem`, which a StorageService
    // does not have, inside a catch-everything — so every read was a TypeError read as "nothing
    // saved", and a returning Spanish visitor opened in English. The real `get` absorbs the browser
    // refusing storage itself (SWALLOWED-CATCH-BURNDOWN-001 U3), so nothing is caught here.
    const saved = storage ? storage.get(storageKey, STORAGE_TYPE_LOCAL) : null;
    if (saved && codes.includes(saved)) return saved;
    if (i18n.language && codes.includes(i18n.language)) return i18n.language;
    return languages[0].code;
  }, [languages, storageKey, storage, STORAGE_TYPE_LOCAL, i18n.language, initialLocale]);

  const [currentLang, setCurrentLang] = useState(initialLang);
  const [activeIndex, setActiveIndex] = useState(() =>
    languages.findIndex((l) => l.code === initialLang),
  );

  // Apply the language to i18n if it differs from the current i18n
  // language (e.g. user picked Spanish in a prior session). The guarded
  // condition `i18n.language !== currentLang` makes the re-run safe:
  // when handleSelect/storage-event update currentLang, they also call
  // i18n.changeLanguage synchronously, so i18n.language === currentLang
  // when the effect re-fires — making it a no-op without a mount latch.
  useEffect(() => {
    if (currentLang && i18n.language !== currentLang) {
      i18n.changeLanguage(currentLang);
    }
  }, [currentLang, i18n]);

  // Follow a language change made ELSEWHERE (Q-LC24-7). The switcher's current language is set on
  // its first render, and on a hydrated page that render is in the DOCUMENT's language — the mount
  // holds i18n there so the markup matches, then moves it to the visitor's. Without this the
  // switcher kept the document's language and its effect above changed i18n back to it: the page
  // hydrated cleanly and then snapped back to English. A language the switcher does not offer
  // leaves it where it is.
  useEffect(() => {
    const follow = (code) => {
      const index = languages.findIndex((l) => l.code === code);
      if (index !== -1) {
        setCurrentLang(code);
        setActiveIndex(index);
      }
    };
    i18n.on('languageChanged', follow);
    return () => i18n.off('languageChanged', follow);
  }, [i18n, languages]);

  // Cross-tab sync: listen for the localStorage 'storage' event so a
  // language change in another tab updates this tab's switcher too.
  useEffect(() => {
    // No `typeof window === 'undefined'` guard: useEffect callbacks only run
    // client-side (never during SSR), so `window` is always defined here.
    const handler = (event) => {
      if (event.key !== storageKey) return;
      const codes = languages.map((l) => l.code);
      if (event.newValue && codes.includes(event.newValue) && event.newValue !== currentLang) {
        setCurrentLang(event.newValue);
        setActiveIndex(codes.indexOf(event.newValue));
        i18n.changeLanguage(event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [storageKey, languages, currentLang, i18n]);

  return { currentLang, setCurrentLang, activeIndex, setActiveIndex };
}

/**
 * useLocaleMenu — the "is the listbox open" half of the switcher: the open flag
 * the popover factory drives, the roving-focus landing spot on open, and the
 * ArrowUp/ArrowDown-opens nicety. Closing is the FACTORY's (R6-B session 12 U2,
 * Q-R6B-25): the render-prop `close()` on a pick — focus to the trigger
 * synchronously, then closed — and `returnFocusOnClose` for every other close
 * (Escape, outside press). The hand-rolled `closeMenu` = `setIsOpen(false)` +
 * `buttonRef.focus()` was a second way to do the same thing, in the wrong
 * order (the commit ran with the OPTION still focused) and blind to Escape.
 *
 * @returns {{isOpen: boolean, setIsOpen: Function, handleButtonKeyDown: Function}}
 */
function useLocaleMenu({ itemRefs, activeIndex }) {
  const [isOpen, setIsOpen] = useState(false);

  // Focus the active option whenever the popover opens (mouse click OR the
  // ArrowUp/ArrowDown open path). The factory owns the open toggle + dismiss;
  // this only manages the roving-focus landing spot. setTimeout(0) waits for
  // the popover to render the option elements before focusing.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      itemRefs.current[activeIndex]?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen, activeIndex, itemRefs]);

  // ArrowUp/ArrowDown on the (closed) trigger opens the listbox — a WAI-ARIA
  // listbox-button nicety. Capture phase: the popover factory spreads
  // floating-ui's own bubble `onKeyDown` (Enter/Space open) onto the trigger,
  // which would clobber a bubble-phase handler here; `onKeyDownCapture` survives
  // the spread and runs first.
  const handleButtonKeyDown = useCallback((event) => {
    if (event.key === KEY_ARROW_DOWN || event.key === KEY_ARROW_UP) {
      event.preventDefault();
      setIsOpen(true);
    }
  }, []);

  return { isOpen, setIsOpen, handleButtonKeyDown };
}

/**
 * useLocaleSwitcher — the switcher's whole behavior, composed from the language
 * half + the menu half, plus the commit action (`handleSelect`) that spans both.
 * <LocaleSwitcher> below is then a pure render of what this returns.
 */
function useLocaleSwitcher({
  languages,
  storageKey,
  storage,
  STORAGE_TYPE_LOCAL,
  onChange,
  initialLocale,
  navigate,
  defaultLocale,
  i18n,
}) {
  const itemRefs = useRef([]);

  const { currentLang, setCurrentLang, activeIndex, setActiveIndex } = useLocaleLanguage({
    languages,
    storageKey,
    storage,
    STORAGE_TYPE_LOCAL,
    initialLocale,
    i18n,
  });

  const { isOpen, setIsOpen, handleButtonKeyDown } = useLocaleMenu({ itemRefs, activeIndex });

  // `close` is the factory's item-pick seam (the render-prop's): called FIRST, so focus is on
  // the trigger while the language commits — and a route change, `onChange` or a consumer's
  // dialog captures the trigger, not an option about to unmount.
  const handleSelect = useCallback(
    (code, close) => {
      close();
      if (code === currentLang) return;
      setCurrentLang(code);
      setActiveIndex(languages.findIndex((l) => l.code === code));
      // This write called `setItem`, which a StorageService does not have, inside a
      // catch-everything — so the switcher NEVER saved the visitor's choice and nothing said so.
      // The real `set` on the site's instance absorbs a refused write (private browsing, a full
      // quota) itself; a throw here is a defect and surfaces (SWALLOWED-CATCH-BURNDOWN-001 U3).
      if (storage) {
        storage.set(storageKey, code, STORAGE_TYPE_LOCAL);
      }
      i18n.changeLanguage(code);
      // A consumer's callback that throws is a defect in that consumer; it surfaces
      // rather than disappearing here.
      if (typeof onChange === 'function') {
        onChange(code);
      }
      // Multilingual content engine — blog-path-aware navigation:
      // when the user picks a locale on a blog route, also rewrite the
      // URL so the consumer serve layer fetches the sibling-row in the
      // target locale. Non-blog routes keep change-language-only
      // behavior (the page's i18n bundle handles the chrome flip).
      // SSR-safe: `window` may be undefined during prerender (the
      // server harness never reaches handleSelect anyway, but guard
      // for defense-in-depth).
      if (
        typeof window !== 'undefined' &&
        window.location &&
        isBlogPath(window.location.pathname)
      ) {
        // No catch: the path helpers are pure string functions, and a same-origin
        // path handed to the router or to `location.assign` does not throw. A throw
        // here is a defect, and it surfaces.
        const { path } = stripLocalePrefix(window.location.pathname, defaultLocale);
        const targetUrl = withLocalePrefix(path, code, defaultLocale);
        const finalUrl = `${targetUrl}${window.location.search || ''}${window.location.hash || ''}`;
        if (typeof navigate === 'function') {
          navigate(finalUrl);
        } else {
          window.location.assign(finalUrl);
        }
      }
    },
    [
      currentLang,
      setCurrentLang,
      setActiveIndex,
      languages,
      storageKey,
      storage,
      STORAGE_TYPE_LOCAL,
      i18n,
      onChange,
      navigate,
      defaultLocale,
    ],
  );

  // Per-item key handling that is ORTHOGONAL to the factory's roving navigation:
  // Arrow/Home/End now bubble to the listbox's factory keydown handler (from
  // `itemProps` + the floating element), so only Enter/Space activation is kept
  // local — selecting the focused option commits the language change. Other keys
  // fall through (Tab leaves the listbox, etc.).
  const handleItemKeyDown = useCallback(
    (event, index, close) => {
      if (event.key === KEY_ENTER || event.key === KEY_SPACE) {
        event.preventDefault();
        handleSelect(languages[index].code, close);
      }
    },
    [languages, handleSelect],
  );

  return {
    itemRefs,
    currentLang,
    activeIndex,
    setActiveIndex,
    isOpen,
    setIsOpen,
    handleSelect,
    handleButtonKeyDown,
    handleItemKeyDown,
  };
}

/**
 * @param {Object} props
 * @param {Array<{code: string, label: string}>} props.languages   REQUIRED, ≥ 2 entries
 * @param {string} props.storageKey                                Storage key for persistence
 * @param {Object} [props.storage]                                The site's ONE `@puffin-core/storage` StorageService
 *                                                                 instance (never the class); omitted on the server
 * @param {string} props.STORAGE_TYPE_LOCAL                        Storage type constant
 * @param {Function} [props.onChange]                              (newLangCode) => void
 *
 * The button's label is marketing-shell's own word (`marketingShell` namespace,
 * `localeSwitcher.label`), not a prop: eight of the nine sites never passed one and showed the English
 * fallback to Spanish readers (LIFE-SHORTLISTED-001 F-Q7-1).
 */
function LocaleSwitcher({
  languages,
  storageKey,
  storage,
  STORAGE_TYPE_LOCAL,
  onChange,
  initialLocale,
  navigate,
  defaultLocale = 'en',
}) {
  const { i18n } = useTranslation();
  const {
    itemRefs,
    currentLang,
    activeIndex,
    setActiveIndex,
    isOpen,
    setIsOpen,
    handleSelect,
    handleButtonKeyDown,
    handleItemKeyDown,
  } = useLocaleSwitcher({
    languages,
    storageKey,
    storage,
    STORAGE_TYPE_LOCAL,
    onChange,
    initialLocale,
    navigate,
    defaultLocale,
    i18n,
  });
  const t = useMarketingShellTranslate();

  // Don't render anything if only one language is configured — the
  // switcher would be a no-op + a confusing affordance.
  if (languages.length < 2) {
    return null;
  }

  const current = languages.find((l) => l.code === currentLang) || languages[0];
  const buttonLabel = t('localeSwitcher.label');

  return (
    <div className="locale-switcher">
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-end"
        modal={false}
        triggerHasPopup="listbox"
        returnFocusOnClose
        listNavigation={{
          listRef: itemRefs,
          activeIndex,
          onNavigate: setActiveIndex,
          loop: true,
          selectedIndex: languages.findIndex((l) => l.code === currentLang),
        }}
      >
        <button
          type="button"
          className="locale-switcher-button"
          aria-label={buttonLabel}
          onKeyDownCapture={handleButtonKeyDown}
        >
          <span>{current.label}</span>
        </button>
        <PopoverContent
          className="locale-switcher-popover"
          ariaLabel={buttonLabel}
          testId="locale-switcher-popover"
        >
          {({ itemProps, close }) => (
            <ul
              className="locale-switcher-menu"
              role="listbox"
              aria-label={buttonLabel}
              tabIndex={-1}
            >
              {languages.map((lang, index) => {
                const isCurrent = lang.code === currentLang;
                return (
                  <li
                    key={lang.code}
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    role="option"
                    aria-selected={isCurrent}
                    tabIndex={index === activeIndex ? 0 : -1}
                    className={
                      isCurrent
                        ? 'locale-switcher-option locale-switcher-option-current'
                        : 'locale-switcher-option'
                    }
                    // Spread the factory's per-item props (floating-ui's pointer +
                    // focus wiring for roving nav) first, then our select-on-click
                    // last. Selecting closes the menu immediately, so floating-ui's
                    // intrinsic active-index-on-click is moot — our handler owns the
                    // commit (close() first, then language change + persistence).
                    {...itemProps(index)}
                    onClick={() => handleSelect(lang.code, close)}
                    onKeyDown={(event) => handleItemKeyDown(event, index, close)}
                  >
                    {lang.label}
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

LocaleSwitcher.propTypes = {
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  storageKey: PropTypes.string.isRequired,
  storage: PropTypes.shape({ get: PropTypes.func.isRequired, set: PropTypes.func.isRequired }),
  STORAGE_TYPE_LOCAL: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  initialLocale: PropTypes.string,
  /**
   * Optional navigation function — `(url) => void`. When provided AND
   * the user picks a locale on a blog route, the switcher invokes this
   * instead of `window.location.assign(...)` so the host's SPA router
   * (typically `useNavigate` from react-router) handles the change
   * client-side. Non-blog routes never call it (change-language only).
   */
  navigate: PropTypes.func,
  /**
   * Default site locale — locale that owns the canonical (no-prefix)
   * blog URLs (e.g. `/blog/foo`). Non-default locales get the
   * `/<locale>` prefix.
   */
  defaultLocale: PropTypes.string,
};

export default LocaleSwitcher;
